import { useState, useMemo, useCallback, useEffect, useContext, ReactNode } from 'react'
import fp from 'lodash/fp'
import _ from 'lodash'
import { useConceptStatus } from '../../../redux/selectConceptStatus2'
import { useFormDispatch } from '../../../redux/formStoreHooks'
import { getConceptStatusValue } from '@fusion-advantage/formlib'
import { submitStatusUpdates } from '../../../redux/submitStatusUpdates'
import L1ErrorBlock from '../l1/L1ErrorBlock'
import { L3PhysicianList } from '../l3-hardcode/L3PhysicianList'
import L3DMESupplierList, { L3DMESupplierListModel } from '../l3-hardcode/L3DMESupplierList'
import { L3PatientLabList, L3PatientLabListModel } from '../l3-hardcode/L3PatientLabList'
import { L3OutsideLabList, L3OutsideLabListModel } from '../l3-hardcode/L3OutsideLabList'
import { L3SurgicalHistoryList, L3SurgicalHistoryListModel } from '../l3-hardcode/L3SurgicalHistoryList'
import { L3FpoHumanoidList } from '../l3-hardcode/L3FpoHumanoidList'
import { ConceptStatusValueType, ConceptValueDataType } from '@fusion-advantage/backcompat'
import {
	Physician,
	ConceptValueModelList,
	DMESupplierListConcept,
	OutsideLabsAvailableConcept,
	PhysicianListConcept,
	SurgicalHistoryOtherProceduresConcept,
} from '@fusion-advantage/formlib'
import { L4AmputationList } from './L4AmputationReview'
import { useConceptValidator } from './useConceptValidator'
import L6FormContext from '../l6/L6FormContext'
import { L4PrintBound } from './L4Print'

export interface useL4TabularStateReturn<ModelType> {
	editIdx: number|null,
	setEditIdx: (idx: number|null) => void,
	models: ModelType[],
	modelList: ConceptValueModelList<ModelType>,
	addModels: (model: ModelType[], open: boolean) => void,
	updateModel: (modelIdx: number, path: string, value: any) => void,
	deleteModel: (modelIdx: number) => void,
	replaceModels: (models: Array<ModelType>) => void,
}

export function useL4TabularState<ModelType>(cid: string, xpath?: string): useL4TabularStateReturn<ModelType> {
	const [editIdx, setEditIdx] = useState<number|null>(null)
	const dispatch = useFormDispatch()
	const conceptStatus = useConceptStatus(cid)
	const modelList = useMemo(() => {
		return new ConceptValueModelList<ModelType>(getConceptStatusValue(conceptStatus))
	}, [conceptStatus])
	const dispatchModels = useCallback(() => {
		const conceptValues = [{
			cid,
			value: modelList.toJson(),
			valueType: ConceptStatusValueType.Chosen,
		}]
		dispatch(submitStatusUpdates(conceptValues))
	}, [modelList, cid, dispatch])
	const replaceModels = useCallback((newModels: ModelType[]) => {
		modelList.list = newModels
		dispatchModels()
	}, [modelList, dispatchModels])
	const addModels = useCallback((newModels: ModelType[], open: boolean) => {
		modelList.list.push(...newModels)
		dispatchModels()
		if(open) {
			setEditIdx(modelList.list.length - 1)
		}
	}, [modelList, dispatchModels, setEditIdx])
	const updateModel = useCallback((modelIdx: number, path: string, value: any) => {
		replaceModels(fp.set(`[${modelIdx}].${path}`, value, modelList.list))
	}, [replaceModels, modelList])
	const deleteModel = useCallback((modelIdx: number) => {
		if(confirm('Delete this item?')) {
			modelList.removeItemsByIdx([modelIdx])
			dispatchModels()
		}
	}, [modelList, dispatchModels])
	const { setXpathValuedDeterminer } = useContext(L6FormContext)
	const isValued = useCallback(() => {
		return modelList.list.length > 0
	}, [modelList])
	useEffect(() => {
		if(xpath) {
			setXpathValuedDeterminer(xpath, isValued)
			return () => {
				setXpathValuedDeterminer(xpath, false)
			}
		}
	}, [xpath, setXpathValuedDeterminer, isValued])
	return {
		editIdx,
		setEditIdx,
		models: modelList.list,
		modelList,
		addModels,
		updateModel,
		deleteModel,
		replaceModels,
	}
}

interface L4TabularListProps {
	cid?: string
	name: string
	xpath?: string
}

const L4TabularList = ({cid, name, xpath}: L4TabularListProps) => {
	let requireConcept = true
	let simpleListNode = null
	// read-only tables do not require cid;
	// they don't write, and their data comes from elsewhere
	switch(name) {
		case 'Amputation Review':
			requireConcept = false
			simpleListNode = <L4AmputationList xpath={xpath} />
			break
		case 'FPO Humanoid':
			requireConcept = false
			simpleListNode = <L3FpoHumanoidList />
			break
		case 'Physicians':
			cid = PhysicianListConcept.cid
			break
		case 'DME Supplier':
			cid = DMESupplierListConcept.cid
			break
		case 'Outside Labs':
			cid = OutsideLabsAvailableConcept.cid
			break
		case 'Surgical History':
			cid = SurgicalHistoryOtherProceduresConcept.cid
			break
	}
	const { errorNode } = useConceptValidator({
		tagName: 'L4TabularList',
		cid,
		requireConcept,
		allowDataTypes: [ConceptValueDataType.JSON],
	})
	if(simpleListNode) {
		return simpleListNode
	}
	if(errorNode) {
		return errorNode
	}
	let content: ReactNode = null
	switch(name) {
		case 'Physicians':
			content = <L4PhysicianList cid={cid!} xpath={xpath} />
			break
		case 'DME Supplier':
			content = <L4DMESupplierList cid={cid!} xpath={xpath} />
			break
		case 'Patient Labs':
			content = <L4PatientLabList cid={cid!} xpath={xpath} />
			break
		case 'Outside Labs':
			content = <L4OutsideLabList cid={cid!} xpath={xpath} />
			break
		case 'Surgical History':
			content = <L4SurgicalHistoryList cid={cid!} xpath={xpath} />
			break
		default:
			return <L1ErrorBlock content={`[L4TabularList] name '${name}' not found`} />
	}
	if(!xpath) {
		return content
	}
	return (
		<L4PrintBound xpath={xpath}>
			{content}
		</L4PrintBound>
	)
}

const L4PhysicianList = ({cid, xpath}: {cid: string, xpath?: string}) => {
	const tabState = useL4TabularState<Physician>(cid, xpath)
	const updateModel = useCallback((modelIdx: number, path: string, value: any) => {
		// only allow one PCP
		if(path === 'IsPCP' && value === true) {
			_.forEach(tabState.models, (model, idx) => {
				tabState.updateModel(idx, 'IsPCP', (idx === modelIdx))
			})
			return
		}
		tabState.updateModel(modelIdx, path, value)
	}, [tabState])
	return <L3PhysicianList {...tabState} updateModel={updateModel} />
}

const L4DMESupplierList = ({cid, xpath}: {cid: string, xpath?: string}) => {
	const tabState = useL4TabularState<L3DMESupplierListModel>(cid, xpath)
	return <L3DMESupplierList {...tabState} />
}

const L4PatientLabList = ({cid, xpath}: {cid: string, xpath?: string}) => {
	const tabState = useL4TabularState<L3PatientLabListModel>(cid, xpath)
	return <L3PatientLabList {...tabState} />
}

const L4OutsideLabList = ({cid, xpath}: {cid: string, xpath?: string}) => {
	const tabState = useL4TabularState<L3OutsideLabListModel>(cid, xpath)
	return <L3OutsideLabList {...tabState} />
}

const L4SurgicalHistoryList = ({cid, xpath}: {cid: string, xpath?: string}) => {
	const tabState = useL4TabularState<L3SurgicalHistoryListModel>(cid, xpath)
	return <L3SurgicalHistoryList {...tabState} />
}

export default L4TabularList
